import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { IRootState } from '../../../@types/redux';
import { ITransformer } from '../../../@types/model/transformer/transformer';
import TransformerFunctions from '../../../store/transformer/functions';
import { Card, CircularProgress, Fab, FormHelperText, Icon, InputAdornment, Toolbar, Tooltip, Typography } from '@material-ui/core';
import FileDropdown from '../../customComponents/FileDropdown';
import Papa from 'papaparse';
import GeneralFunctions from '../../../store/general/functions';
import MaterialTable from '../../customComponents/materialTable/Table';
import { DatePicker } from '@material-ui/pickers';
import { Moment } from 'moment';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import lodash from 'lodash';
import moment from 'moment';
import { HIGH_SEASON_MONTHS } from '../../../appConstants';
import { IUserSession } from '../../../@types/employee';
import TransformerBudgetHelper, { BudgetType, ITransformerBudget, ISPUBudget, ILPUBudget, IMunicipalityBudget } from '../../../@types/model/transformer/budget/budget';

interface IParsedCsv {
    Waypoint : string;
    'System Month' : string;
    'SPU 2023_24 Budget' : number | null;
    '2023_24 kWh LPU STD Budget' : number | null;
    '2023_24 kWh LPU Peak Budget' : number | null;
    '2023_24 kWh LPU Off Budget' : number | null;
}

interface IBudgetCsv {
    epNumber : string;
    month : string;

    type : BudgetType;
    std : number | null;
    peak : number | null;
    offpeak : number | null;
}

interface ITransformerBudgetCsvProps {
    transformers : Array<ITransformer>;
    isLoadingTransformers : boolean;

    session ?: IUserSession | null;
}

const TransformerBudgetCsvComponent = (props : ITransformerBudgetCsvProps) : React.ReactElement => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [csvFile, setCsvFile] = useState<File | undefined>(undefined);
    const [csvData, setCsvData] = useState<Array<IBudgetCsv>>([]);
    const [date, setDate] = useState<Moment | null>(null);

    useEffect(() => {
        TransformerFunctions.getList();
    }, []);

    const getIsLoading = useMemo<boolean>(() => {
        return props.isLoadingTransformers || isLoading;
    }, [props.isLoadingTransformers, isLoading]);

    const parseCompleted = (result ?: Papa.ParseResult<IParsedCsv>) : void => {
        if (!result) {
            setCsvData([]);
            return;
        }

        if (result.errors.length) {
            GeneralFunctions.generalShowErrorSnackbar(result.errors.map(x => x.message).join(','));
            return;
        }
        
        const data = result.data.slice();
        
        setCsvData(data.map((n) => ({
            epNumber: n.Waypoint,
            month: n['System Month'],
            type: n['SPU 2023_24 Budget'] ? 'spu' : 'lpu',
            offpeak: !n['2023_24 kWh LPU Off Budget'] ? null : Number(n['2023_24 kWh LPU Off Budget']),
            peak: !n['2023_24 kWh LPU Peak Budget'] ? null : Number(n['2023_24 kWh LPU Peak Budget']),
            std: n['SPU 2023_24 Budget'] ? Number(n['SPU 2023_24 Budget']) : Number(n['2023_24 kWh LPU STD Budget']),
        })));
    };

    const parseCsv = (csvFile ?: File) : void => {
        if (csvFile?.type !== 'text/csv') {
            setCsvData([]);
            return;
        }

        Papa.parse<IParsedCsv>(csvFile, {
            header: true,
            complete: parseCompleted,
        });
    };

    const onCsvChange = (file ?: File) : void => {
        setCsvFile(file);
        parseCsv(file);
    };

    const onDateChange = (newDate : MaterialUiPickersDate) : void => {
        setDate(newDate?.asUTC().startOf('year') ?? null);
    };

    const createBudgets = async () : Promise<void> => {
        try {
            if (!csvData.length) return;
            if (!date) return;
            if (!props.transformers.length) return;
            if (!props.session) return;
    
            const resultList : Array<ITransformerBudget> = [];
    
            const companyAreaGroups = lodash
                .chain(props.transformers)
                .filter(x => !!x.Company && !!x.Division && !!x.MeterType)
                .groupBy(x => `${x.Company?.id}_${x.Division}_${x.MeterType}`)
                .value();
    
            lodash.forEach(companyAreaGroups, (values) => {
                const area = values[0].Division;
                const company = values[0].Company;
                const type = values[0].MeterType;
                const startDate = moment(date).month('July');
                const year = moment.utc(startDate).add(1, 'year').subtract(1, 'day').year();
                const endDate = moment.utc(startDate).add(1, 'year').subtract(1, 'day').valueOf();

                const result : ITransformerBudget = {
                    id: `${type}_${company?.id}_${area}_${year}`,
                    isActive: true,
                    createdBy: props.session!.firebaseUser.uid,
                    createdByName: props.session!.firebaseUser.displayName ?? '',
                    createdByEmployee: props.session!.employee.EmployeeNumber ?? '',
                    createdOn: moment.utc().valueOf(),
                    updatedBy: props.session!.firebaseUser.uid,
                    updatedByName: props.session!.firebaseUser.displayName ?? '',
                    updatedByEmployee: props.session!.employee.EmployeeNumber ?? '',
                    updatedOn: moment.utc().valueOf(),
                    isWeb: true,
                    type: type?.toLocaleLowerCase() === 'spu' ? 'spu' : 'lpu',
                    area,
                    companyId: company?.id ?? '',
                    companyName: company?.name ?? '',
                    prevBudget: null,
                    year,
                    startDate: startDate.valueOf(),
                    endDate: endDate.valueOf(),
                    values: [],
                };

                values.forEach((transformer) => {
                    const budgetValues = csvData.filter(x => x.epNumber === transformer.EPNumber);

                    if (!budgetValues.length) return;
                    
                    budgetValues.forEach((budget) => {

                        const month = moment.utc(startDate).month(budget.month);
    
                        if (month.month() <= 5) {
                            month.add('y', 1);
                        }

                        if (budget.type === 'spu') {
                            const budgetResult : ISPUBudget = {
                                budget: budget.std ?? 0,
                                comment: 'CSV Import',
                                days: month.daysInMonth(),
                                month: month.valueOf(),
                                highSeason: HIGH_SEASON_MONTHS.includes(month.month() + 1),
                                transformer: transformer,
                                transformerRef: transformer.ref.id,
                            };
                            result.values.push(budgetResult);
                        }

                        if (budget.type === 'lpu') {
                            const budgetResult : ILPUBudget = {
                                standardBudget: budget.std ?? 0,
                                offpeakBudget: budget.offpeak ?? 0,
                                peakBudget: budget.peak ?? 0,
                                totalBudget: null,
                                comment: 'CSV Import',
                                days: month.daysInMonth(),
                                month: month.valueOf(),
                                highSeason: HIGH_SEASON_MONTHS.includes(month.month() + 1),
                                transformer: transformer,
                                transformerRef: transformer.ref.id,
                            };

                            if (!budgetResult.totalBudget) {
                                budgetResult.totalBudget = 0;
                            }

                            budgetResult.totalBudget += budgetResult.standardBudget ?? 0;
                            budgetResult.totalBudget += budgetResult.offpeakBudget ?? 0;
                            budgetResult.totalBudget += budgetResult.peakBudget ?? 0;

                            result.values.push(budgetResult);
                        }

                        if (budget.type === 'municipality') {
                            const budgetResult : IMunicipalityBudget = {
                                budget: budget.std ?? 0,
                                comment: 'CSV Import',
                                days: month.daysInMonth(),
                                month: month.valueOf(),
                                highSeason: HIGH_SEASON_MONTHS.includes(month.month() + 1),
                                transformer: transformer,
                                transformerRef: transformer.ref.id,
                            };
                            result.values.push(budgetResult);
                        }
                    });
                });

                resultList.push(result);
            });
            
            await TransformerBudgetHelper.batchSave(resultList);

            setCsvData([]);
            setDate(null);
            setCsvFile(undefined);
        } catch (ex) {
            GeneralFunctions.generalShowError(ex, 'Error creating budgets');
        } finally {
            setIsLoading(false);
        }
    };

    const onCreateClick = () : void => {
        setIsLoading(true);
        createBudgets();
    };

    return (
        <div className={'fdc flx1 p10 mh0 mw0 bcg0'}>
            {
                getIsLoading &&
                <div className='fdc flx1 aic jcc'>
                    <CircularProgress />
                </div>
            }
            {
                !getIsLoading &&
                <div className={'flx1 fdc mh0 mw0'}>
                    <Toolbar>
                        <Typography variant='h5' color='inherit'>
                            SELECT A BUDGET CSV
                        </Typography>
                        <div className='fdc flx1 aifs jcfs ml15 mr15'>
                            <FileDropdown
                                label='Select csv'
                                accept={'.csv'}
                                fullWidth
                                value={csvFile}
                                required
                                onChange={onCsvChange}
                            />
                        </div>
                        <div className='fdc flx1 aifs jcfs mr15'>
                            <DatePicker
                                label={'Year'}
                                onChange={onDateChange}
                                value={date}
                                format={'YYYY'}
                                views={['year']}
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end'>
                                            <Icon>date_range</Icon>
                                        </InputAdornment>
                                    ),
                                }}
                                error={!date}
                                required
                            />
                            {
                                !date &&
                                <FormHelperText error>Required</FormHelperText>
                            }
                        </div>
                        <div>
                            <Tooltip title={'Create'}>
                                <div>
                                    <Fab
                                        size='small'
                                        color='secondary'
                                        className='bcy'
                                        variant='extended'
                                        aria-label='Create'
                                        disabled={!date || !csvData.length}
                                        onClick={onCreateClick}
                                    >
                                        <span className='pl10 pr10'>Create</span>
                                    </Fab>
                                </div>
                            </Tooltip>
                        </div>
                        <span className={'flx4'} />
                    </Toolbar>
                    <Card className={'flx1 fdc'}>
                        <MaterialTable<IBudgetCsv>
                            id='csvData'
                            data={csvData}
                            isLoading={getIsLoading}
                            rowsPerPage={100}
                            columns={[{
                                header: 'Waypoint',
                                field: 'epNumber',
                                width: 150,
                                enableSort: false,
                                enableFilter: false,
                            }, {
                                header: 'System Month',
                                field: 'month',
                                width: 145,
                                enableSort: false,
                                enableFilter: false,
                            }, {
                                header: 'Type',
                                field: 'type',
                                width: 175,
                                enableSort: false,
                                enableFilter: false,
                            }, {
                                header: 'STD',
                                field: 'std',
                                width: 175,
                                enableSort: false,
                                enableFilter: false,
                            }, {
                                header: 'PEAK',
                                field: 'peak',
                                width: 175,
                                enableSort: false,
                                enableFilter: false,
                            }, {
                                header: 'OFF PEAK',
                                field: 'offpeak',
                                width: 175,
                                enableSort: false,
                                enableFilter: false,
                            }]}
                        />
                    </Card>
                </div>
            }
        </div>
    );
};

const mapStateToProps = (state : IRootState) => {
    return {
        transformers: state.transformer.transformers,
        isLoadingTransformers: state.transformer.loading,
        session: state.auth.session,
    };
};

const TransformerBudgetCsv = connect(
    mapStateToProps,
)(TransformerBudgetCsvComponent);

export default TransformerBudgetCsv;
